import React, { useState, useEffect, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { fallbackWrongKey } from '../../lib/speechAnswerEvaluator';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './AnimalBabies.css';

/* eslint-disable react-hooks/exhaustive-deps */

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const ANIMALS_DICT = {
  cat: { adultName: 'Кошка', adultGenitive: 'кошки', babyName: 'Котёнок', adultImg: 'cat', babyImg: 'kitten', question: 'Кто детёныш у кошки?' },
  dog: { adultName: 'Собака', adultGenitive: 'собаки', babyName: 'Щенок', adultImg: 'dog', babyImg: 'puppy', question: 'Кто детёныш у собаки?' },
  cow: { adultName: 'Корова', adultGenitive: 'коровы', babyName: 'Телёнок', adultImg: 'cow', babyImg: 'calf', question: 'Кто детёныш у коровы?' },
  horse: { adultName: 'Лошадь', adultGenitive: 'лошади', babyName: 'Жеребёнок', adultImg: 'horse', babyImg: 'foal', question: 'Кто детёныш у лошади?' },
  bear: { adultName: 'Медведь', adultGenitive: 'медведя', babyName: 'Медвежонок', adultImg: 'bear', babyImg: 'bear_cub', question: 'Кто детёныш у медведя?' },
  wolf: { adultName: 'Волк', adultGenitive: 'волка', babyName: 'Волчонок', adultImg: 'wolf', babyImg: 'wolf_cub', question: 'Кто детёныш у волка?' },
  squirrel: { adultName: 'Белка', adultGenitive: 'белки', babyName: 'Бельчонок', adultImg: 'squarel', babyImg: 'squirrel_cub', question: 'Кто детёныш у белки?' },
  pig: { adultName: 'Свинья', adultGenitive: 'свиньи', babyName: 'Поросёнок', adultImg: 'pig', babyImg: 'pig_cub', question: 'Кто детёныш у свиньи?' },
  hen: { adultName: 'Курица', adultGenitive: 'курицы', babyName: 'Цыплёнок', adultImg: 'hen', babyImg: 'chicken', question: 'Кто детёныш у курицы?' },
  elephant: { adultName: 'Слон', adultGenitive: 'слона', babyName: 'Слонёнок', adultImg: 'elephant', babyImg: 'elephant_cub', question: 'Кто детёныш у слона?' },
  hare: { adultName: 'Заяц', adultGenitive: 'зайца', babyName: 'Зайчонок', adultImg: 'hare', babyImg: 'hare_cub', question: 'Кто детёныш у зайца?' },
  tiger: { adultName: 'Тигр', adultGenitive: 'тигра', babyName: 'Тигрёнок', adultImg: 'tiger', babyImg: 'tiger_cub', question: 'Кто детёныш у тигра?' },
  fox: { adultName: 'Лиса', adultGenitive: 'лисы', babyName: 'Лисёнок', adultImg: 'fox', babyImg: 'fox_cub', question: 'Кто детёныш у лисы?' },
  deer: { adultName: 'Олень', adultGenitive: 'оленя', babyName: 'Оленёнок', adultImg: 'olen', babyImg: 'olen_cub', question: 'Кто детёныш у оленя?' },
  duck: { adultName: 'Утка', adultGenitive: 'утки', babyName: 'Утёнок', adultImg: 'duck', babyImg: 'duckling', question: 'Кто детёныш у утки?' },
  lion: { adultName: 'Лев', adultGenitive: 'льва', babyName: 'Львёнок', adultImg: 'lion', babyImg: 'lion_cub', question: 'Кто детёныш у льва?' }
};

const ANIMALS_BY_AGE = {
  '4': ['cat', 'dog', 'cow', 'horse', 'bear', 'wolf', 'squirrel', 'pig'],
  '5': ['cat', 'dog', 'cow', 'horse', 'bear', 'wolf', 'squirrel', 'pig'],
  '6': ['hen', 'elephant', 'hare', 'tiger', 'lion', 'fox', 'deer', 'duck'],
  '7-9': ['hen', 'elephant', 'hare', 'tiger', 'lion', 'fox', 'deer', 'duck'],
  '10-12': ['hen', 'elephant', 'hare', 'tiger', 'lion', 'fox', 'deer', 'duck']
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const AnimalBabies = ({ ageGroup, onBack, onComplete }) => {
  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('AnimalBabies');
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();

  const animalKeys = ANIMALS_BY_AGE[ageGroup] || ANIMALS_BY_AGE['4'];

  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const [globalIndex, setGlobalIndex] = useState(0);
  const [preparedPages, setPreparedPages] = useState([]);
  
  const [answers, setAnswers] = useState([]);
  const [completedLog, setCompletedLog] = useState([]);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [hasSpoken, setHasSpoken] = useState(false);

  const initExercise = useCallback(() => {
    const keys = ANIMALS_BY_AGE[ageGroup] || ANIMALS_BY_AGE['4'];
    const chunks = chunkArray(keys, 4);
    
    const pagesData = chunks.map(pageKeys => ({
      adults: pageKeys.map(k => ({ id: k, name: ANIMALS_DICT[k].adultName, img: ANIMALS_DICT[k].adultImg, question: ANIMALS_DICT[k].question })),
      babies: pageKeys.map(k => ({ id: ANIMALS_DICT[k].babyName, parentId: k, name: ANIMALS_DICT[k].babyName, img: ANIMALS_DICT[k].babyImg }))
                 .sort(() => Math.random() - 0.5) 
    }));

    setPreparedPages(pagesData);
    setGlobalIndex(0);
    setAnswers([]);
    setCompletedLog([]);
    setActiveFeedback(null);
    setHasSpoken(false);
    setSelectedMode(null);
  }, [ageGroup]);

  useEffect(() => {
    initExercise();
  }, [initExercise]);

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const currentPageIndex = Math.floor(globalIndex / 4);
  const currentAnimalIndex = globalIndex % 4;
  const currentPage = preparedPages[currentPageIndex] || { adults: [], babies: [] };
  const currentQuestion = currentPage.adults[currentAnimalIndex];
  const shuffledBabies = currentPage.babies || [];

  // Озвучка + (в режиме "Говорить") запуск распознавателя — оба запускаются
  // одновременно, а не распознаватель только после озвучки, чтобы он был
  // готов слушать сразу же, как только ребёнок услышит вопрос.
  useEffect(() => {
    if (currentQuestion && !hasSpoken && selectedMode) {
      setHasSpoken(true);
      setIsListening(true);
      setIsSpeaking(false);
      setRecognizerReady(false);

      if (selectedMode === 'speak') {
        startSpeakRecognition(
          shuffledBabies.map((baby) => ({ key: baby.id, text: baby.name })),
          {
            timeoutMs: 12000,
            onListening: () => setRecognizerReady(true),
            onResult: ({ matchedKey }) => {
              const babyCandidates = shuffledBabies.map((baby) => ({ key: baby.id }));
              const correctBabyId = shuffledBabies.find((b) => b.parentId === currentQuestion.id)?.id;
              const resolvedKey = matchedKey || fallbackWrongKey(babyCandidates, correctBabyId);
              const baby = shuffledBabies.find((b) => b.id === resolvedKey);
              if (baby) handleBabyClick(baby);
            },
          }
        );
      }

      playInstruction(currentQuestion.question, () => {
        setIsListening(false);
        setIsSpeaking(true);
      });
    }
  }, [currentQuestion, hasSpoken, selectedMode]);

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
  };

  // ===== НОВАЯ ФУНКЦИЯ: ПОВТОР ВОПРОСА =====
  const repeatInstruction = () => {
    if (!currentQuestion || !selectedMode) return;
    stopInstruction();
    playInstruction(currentQuestion.question);
  };

  const handleBabyClick = (baby) => {
    if (!currentQuestion || activeFeedback) return;

    stopSpeakRecognition();
    stopInstruction();
    setIsSpeaking(false);
    const isCorrect = baby.parentId === currentQuestion.id;

    setActiveFeedback({ id: baby.id, type: isCorrect ? 'correct' : 'wrong' });

    const newEntry = { parent: currentQuestion.id, baby: baby.id, correct: isCorrect };
    const newCompletedLog = [...completedLog, newEntry];
    setCompletedLog(newCompletedLog);
    setAnswers(prev => [...prev, isCorrect]);

    const delay = isCorrect ? 1200 : 1000;

    setTimeout(() => {
      setActiveFeedback(null);
      setHasSpoken(false); 

      if (globalIndex + 1 < animalKeys.length) {
        setGlobalIndex(prev => prev + 1);
      } else {
        if (onComplete) {
          const correctCount = newCompletedLog.filter(e => e.correct).length;
          const failedAnimals = newCompletedLog
            .filter(e => !e.correct)
            .map(e => ANIMALS_DICT[e.parent].adultGenitive);

          onComplete({
            testId: 'AnimalBabies',
            score: Math.round((correctCount / animalKeys.length) * 100),
            details: { total: animalKeys.length, correct: correctCount, failedAnimals },
          });
        } else {
          onBack();
        }
      }
    }, delay);
  };

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={animalKeys.length}
      forceStop={true} 
      onComplete={onComplete || onBack} 
      onRepeat={repeatInstruction}   
      disabled={!selectedMode}
    >
      <div className="ab-game-area">
        
        <div className="ab-top-controls">
          {!selectedMode ? (
            <div className="ab-mode-selection">
              <h2 className="ab-mode-title">Выбери режим</h2>
              <div className="ab-mode-buttons">
                <button 
                  className="ab-mode-btn" 
                  onClick={() => handleModeSelect('speak')}
                  style={{
                    borderColor: blinkTarget === 'speak' ? '#7B4EF8' : '#E5E0F5',
                    background: blinkTarget === 'speak' ? '#F0EDFF' : '#F8F6FF',
                    boxShadow: blinkTarget === 'speak' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                  }}
                >
                  🎙️ Говорить
                </button>
                <button 
                  className="ab-mode-btn" 
                  onClick={() => handleModeSelect('show')}
                  style={{
                    borderColor: blinkTarget === 'show' ? '#7B4EF8' : '#E5E0F5',
                    background: blinkTarget === 'show' ? '#F0EDFF' : '#F8F6FF',
                    boxShadow: blinkTarget === 'show' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                  }}
                >
                  👆 Показать
                </button>
              </div>
            </div>
          ) : (
            <div className="ab-indicators">
              <div className={`ab-indicator listen ${isListening ? 'active' : ''}`}>
                <span>🎧</span>
              </div>
              <div className={`ab-indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}>
                <span>🗣️</span>
              </div>
            </div>
          )}
        </div>

        <div className="ab-animals-grid ab-adults-grid">
          {currentPage.adults.map((animal) => (
            <div key={animal.id} className="ab-animal-card">
              <img 
                src={`${S3_BASE_URL}/${animal.img}.png`} 
                alt={animal.name} 
                className="ab-animal-img"
                onError={(e) => { e.target.src = '/placeholder.png'; }} 
              />
            </div>
          ))}
        </div>

        <div className="ab-animals-grid">
          {shuffledBabies.map((baby) => (
            <div 
              key={baby.id} 
              className={`ab-animal-card ab-baby-card ${activeFeedback?.id === baby.id ? activeFeedback.type : ''}`} 
              onClick={() => handleBabyClick(baby)}
            >
              <img 
                src={`${S3_BASE_URL}/${baby.img}.png`} 
                alt={baby.name} 
                className="ab-animal-img"
                onError={(e) => { e.target.src = '/placeholder.png'; }} 
              />
            </div>
          ))}
        </div>

      </div>
    </ModuleTemplate>
  );
};

export default AnimalBabies; 